/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getAnomalies, getIncidents, getOverview } from "@/api/overview";
import { notificationsApi } from "@/api/notifications";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { IncidentsView } from "@/components/homepage/IncidentsModal";
import { CenterTab, useNotificationCenter } from "@/context/notificationCenter";
import { ProblemsPanel } from "./ProblemsPanel";
import { SentPanel } from "./SentPanel";
import { useAuth } from "@/context/auth";
import { cn } from "@/lib/utils";

const DAY = 86_400_000;

/** The notification centre: open problems, incident history and what was sent, in one side panel. */
export const NotificationCenter: React.FC = () => {
  const { open: wantOpen, tab, focus, closeCenter, setTab } = useNotificationCenter();
  // a link can arrive before the sign-in: the panel waits for it
  const { isAuthenticated } = useAuth();
  const open = wantOpen && isAuthenticated;

  // only while it is open: the login page must not fetch anything
  const { data: overview } = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: 30_000, enabled: open });
  const { data: anomalies = [] } = useQuery({ queryKey: ["anomalies"], queryFn: getAnomalies, refetchInterval: 30_000, enabled: open });
  const { data: incidents = [] } = useQuery({ queryKey: ["incidents"], queryFn: getIncidents, refetchInterval: 30_000, enabled: open });
  const { data: sent, dataUpdatedAt: sentAt } = useQuery({
    queryKey: ["notification-history"],
    queryFn: () => notificationsApi.getHistory(200),
    refetchInterval: 30_000,
    enabled: open,
  });

  const problemCount = (overview?.problems.length ?? 0) + anomalies.length;
  const hasCrit = overview?.problems.some((p) => p.severity === "crit") ?? false;
  const failed = (sent ?? []).filter((r) => !r.success && sentAt - new Date(r.created_at).getTime() < DAY).length;

  const tabs: { id: CenterTab; label: string; count?: number; tone?: "crit" | "warn" }[] = [
    { id: "problems", label: "Problems", count: problemCount, tone: hasCrit ? "crit" : "warn" },
    { id: "history", label: "History" },
    { id: "sent", label: "Sent", count: failed, tone: "crit" },
  ];

  return (
    <Sheet open={open} onOpenChange={(o) => !o && closeCenter()}>
      <SheetContent
        side="right"
        className="nc-sheet gap-0 p-0 sm:max-w-[560px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader className="nc-head">
          <SheetTitle>Notifications</SheetTitle>
          <SheetDescription className="sr-only">Open problems, incident history and what was sent</SheetDescription>
        </SheetHeader>
        <div className="nc-tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={cn("gh-btn", tab === t.id && "gh-btn--on")}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.count ? <span className={cn("nc-count", t.tone && `nc-count--${t.tone}`)}>{t.count}</span> : null}
            </button>
          ))}
        </div>
        <div className="nc-body">
          {tab === "problems" && <ProblemsPanel focus={focus} />}
          {tab === "history" && <IncidentsView incidents={incidents} />}
          {tab === "sent" && <SentPanel />}
        </div>
      </SheetContent>
    </Sheet>
  );
};
